/* eslint-disable @typescript-eslint/no-explicit-any */
import { Add } from "@mui/icons-material";
import { Button, ToggleButton, Tooltip, useMediaQuery } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { deleteData } from "../../Services/deleteData";
import { getData } from "../../Services/getData";
import styles from "../../Styles/dashboard.module.css";
import Config, { dayPilotFormat } from "../../Utils/Config";
import { Vocabulary } from "../../Utils/Vocabulary";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import GenericConfirmModal from "./GenericConfirmModal";
import EditDeleteButtons from "./EditDeleteButtons";
import useQueryStringParser from "./useQueryStringParser";
import moment, { Moment } from "moment";
import { StockTransferStatuses } from "../../Utils/AutocompleteUtils";
import {
  getRequestUrlForMainTable,
  getTextColorBasedOnBg,
} from "../../Utils/Utils";
import { RenderAddComponent } from "../Products/Stocks/StockManagement/StockTransfers";
import { CacheWebsocketContext } from "../../Contexts/WebsocketContext";
import { MUIDataTableState } from "mui-datatables";
import { toast } from "react-toastify";
import { HttpStatusCode } from "../../Utils/Constants";

type AnotherButtonsOptions = {
  condition: (item: any) => boolean;
  title: string;
  icon: any;
  action: (item: any) => void;
};

type HideOptionButtons = {
  add?: boolean;
  edit?: {
    condition: (item: any) => boolean;
  };
  delete?: {
    condition: (item: any) => boolean;
  };
};

type CustomStyle = {
  tableStyle?: any;
  hideCrtNumber?: string;
  colorForRaw?: (item: any) => string;
};

type DeleteMessages = {
  title: string;
  text: string;
};

type TableCustomOptions = {
  showItem?: boolean;
  showSearch?: boolean;
  expandableRows?: boolean;
  selectableRows?: string;
  enableSort?: boolean;
};

type MainTableProps = {
  urlEnumApi: string;
  header: any;
  tableTitle: string;
  shouldUpdate?: boolean | number;
  deleteMessages?: DeleteMessages;
  tableCustomOptions?: TableCustomOptions;
  hideOptionButtons?: HideOptionButtons;
  anotherOptionButtons?: Array<AnotherButtonsOptions>;
  headerButtons?: any;
  customStyle?: CustomStyle;
  handleEdit?: (item: any, showItem?: any) => void;
  renderExpandableRow?: (rowData: any, rowMeta: any, data: any) => void;
  onRowsSelect?: (selectedIds: Array<string>) => void;
  customToolbarSelect?: () => void;
  isRowExpandable?: (dataIndex: any, expandedRows: any, data: any) => boolean;
  onTableDataChange?: (items: Array<any>) => void;
};

type StateType = {
  page: number;
  perPage: number;
  search: string | null;
  sort: string;
  items: any;
  count: number;
  startDate: Moment;
  endDate: Moment;
  order: string | null;
  orderStatus: string | null;
  user: string | null;
  customer: string | null;
  vendor: string | null;
  device: string | null;
  workflow: string | null;
  workflowStatus: string | null;
  step: string | null;
  product: string | null;
  inventory: string | null;
  stockStatus: string | null;
  type: string | null;
  category: string | null;
  manufacturer: string | null;
  tags: string | null;
};

export default function MainTable(props: MainTableProps) {
  const {
    urlEnumApi,
    header,
    tableTitle,
    deleteMessages,
    shouldUpdate,
    tableCustomOptions,
    headerButtons,
    hideOptionButtons,
    customStyle,
    anotherOptionButtons,
    handleEdit,
    renderExpandableRow,
    onRowsSelect,
    customToolbarSelect,
    isRowExpandable,
    onTableDataChange,
  } = props;
  const phoneStyle = useMediaQuery("(max-width: 800px)");
  const websocketContext = useContext(CacheWebsocketContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletedId, setDeletedId] = useState<string | null>(null);
  const [columnDisplayState, setColumnDisplayState] = useState<
    Record<number, boolean>
  >({});
  const [state, setState] = useState<StateType>({
    page: 0,
    perPage: 20,
    search: "",
    sort: "orderNumber,desc",
    items: [],
    count: 0,
    startDate: moment().startOf("year"),
    endDate: moment().date(new Date().getDate()),
    order: null,
    orderStatus: null,
    user: null,
    customer: null,
    vendor: null,
    device: null,
    workflow: null,
    workflowStatus: null,
    step: null,
    product: null,
    inventory: null,
    stockStatus: null,
    type: null,
    category: null,
    manufacturer: null,
    tags: null,
  });

  useEffect(() => {
    if (shouldUpdate) {
      getItems(
        state.page,
        state.perPage,
        state.sort,
        state.search,
        state.startDate,
        state.endDate,
        state.order,
        state.orderStatus,
        state.user,
        state.customer,
        state.vendor,
        state.device,
        state.workflow,
        state.workflowStatus,
        state.step,
        state.product,
        state.inventory,
        state.stockStatus,
        state.type,
        state.category,
        state.manufacturer,
        state.tags
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUpdate]);

  /**
   *
   */
  useQueryStringParser((data: any) => {
    getItems(
      data.page ? parseInt(data.page) : state.page,
      data.perPage ? parseInt(data.perPage) : state.perPage,
      data.sort ? data.sort : state.sort,
      data.search,
      data.startDate ? moment(data.startDate, dayPilotFormat) : state.startDate,
      data.endDate ? moment(data.endDate, dayPilotFormat) : state.endDate,
      data.order ? data.order : null,
      data.orderStatus ? data.orderStatus : null,
      data.user ? data.user : null,
      data.customer ? data.customer : null,
      data.vendor ? data.vendor : null,
      data.device ? data.device : null,
      data.workflow ? data.workflow : null,
      data.workflowStatus ? data.workflowStatus : null,
      data.step ? data.step : null,
      data.product ? data.product : null,
      data.inventory ? data.inventory : null,
      data.stockStatus ? data.stockStatus : null,
      data.type ? data.type : null,
      data.category ? data.category : null,
      data.manufacturer ? data.manufacturer : null,
      data.tags ? data.tags : null
    );
  });

  /**
   *
   * @param filter
   */
  const getItems = (
    page: number,
    perPage: number,
    sort: string,
    search: string | null,
    startDate: Moment,
    endDate: Moment,
    order: string | null,
    orderStatus: string | null,
    user: string | null,
    customer: string | null,
    vendor: string | null,
    device: string | null,
    workflow: string | null,
    workflowStatus: string | null,
    step: string | null,
    product: string | null,
    inventory: string | null,
    stockStatus: string | null,
    type: string | null,
    category: string | null,
    manufacturer: string | null,
    tags: string | null
  ) => {
    const url = getRequestUrlForMainTable(
      urlEnumApi,
      page,
      perPage,
      sort,
      search,
      startDate,
      endDate,
      order,
      orderStatus,
      user,
      customer,
      vendor,
      device,
      workflow,
      workflowStatus,
      step,
      product,
      inventory,
      stockStatus,
      type,
      category,
      manufacturer,
      tags
    );

    getData(url).then((res: any) => {
      if (res) {
        setState({
          ...state,
          items: res.data.result,
          count: res.data.count,
          search: search,
          page: page,
          perPage: perPage,
          sort: sort,
          startDate: startDate,
          endDate: endDate,
          order: order,
          orderStatus: orderStatus,
          user: user,
          customer: customer,
          vendor: vendor,
          device: device,
          workflow: workflow,
          workflowStatus: workflowStatus,
          step: step,
          product: product,
          inventory: inventory,
          stockStatus: stockStatus,
          category: category,
          type: type,
          manufacturer: manufacturer,
          tags: tags,
        });

        // Call the callback if provided
        if (onTableDataChange) {
          onTableDataChange(res.data.result);
        }
      }
    });
  };

  /**
   *
   * @param name
   * @param value
   */
  const addParamToQueryString = (name: any, value: any) => {
    searchParams.set(name, value);
    setSearchParams(searchParams);
  };

  /**
   *
   * @param name
   * @param value
   */
  const removeParamFromQueryString = (name: any) => {
    name.map((param: any) => searchParams.delete(param));
    setSearchParams(searchParams);
  };

  /**
   *
   */
  function handleDeleteModal() {
    setOpenDeleteModal(!openDeleteModal);
  }

  /**
   *
   * @param item
   * @returns
   */
  function showAnotherButtons(item: any) {
    if (anotherOptionButtons) {
      return anotherOptionButtons.map((anotherOptionButton) => {
        if (anotherOptionButton.condition(item))
          return (
            <Tooltip title={anotherOptionButton.title}>
              <ToggleButton
                value="center"
                aria-label="centered"
                onClick={(e: any) => {
                  e.stopPropagation();
                  e.preventDefault();
                  anotherOptionButton.action(item);
                }}
              >
                {anotherOptionButton.icon}
              </ToggleButton>
            </Tooltip>
          );
        return null;
      });
    }
    return null;
  }
  /**
   *
   * @param id
   */
  const deleteItem = (id: string) => {
    deleteData(`${urlEnumApi}/${id}`).then((res: any) => {
      if (res) {
        // Check if the backend returned an error status
        if (res.data && res.data.status !== HttpStatusCode.OK) {
          toast.error(res.data.message || Vocabulary.error);
          return;
        }
        websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
        getItems(
          state.page,
          state.perPage,
          state.sort,
          state.search,
          state.startDate,
          state.endDate,
          state.order,
          state.orderStatus,
          state.user,
          state.customer,
          state.vendor,
          state.device,
          state.workflow,
          state.workflowStatus,
          state.step,
          state.product,
          state.inventory,
          state.stockStatus,
          state.type,
          state.category,
          state.manufacturer,
          state.tags
        );
      }
    });
  };

  const tableHeader = useMemo(() => {
    const baseHeader = [
      {
        label: Vocabulary.crtNo,
        name: "",
        options: {
          sort: false,
          display:
            customStyle && customStyle.hideCrtNumber
              ? customStyle.hideCrtNumber
              : true,
          customBodyRender: (value: any, meta: any) => {
            return (
              <span
                style={{
                  color:
                    meta.rowData[6] && meta.rowData[6].alert
                      ? getTextColorBasedOnBg(meta.rowData[6].alert?.color)
                      : "black",
                }}
              >
                {state.page * state.perPage + meta.rowIndex + 1}
              </span>
            );
          },
        },
      },
      ...header,
      {
        label: Vocabulary.options,
        name: Vocabulary.options,
        options: {
          filter: false,
          setCellHeaderProps: () => ({
            align: "center",
          }),
          setCellProps: () => ({
            align: "center",
          }),
          sort: false,
          empty: true,
          customBodyRenderLite: (dataIndex: any, rowIndex: any) => {
            return (
              <>
                <EditDeleteButtons
                  onEdit={(e: any) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (handleEdit) {
                      if (
                        state.items[rowIndex].status ===
                        StockTransferStatuses.outtake
                      )
                        handleEdit(
                          state.items[rowIndex],
                          RenderAddComponent.outtake
                        );
                      else handleEdit(state.items[rowIndex]);
                    }
                  }}
                  hideEditButton={
                    hideOptionButtons && hideOptionButtons.edit
                      ? hideOptionButtons.edit.condition(state.items[rowIndex])
                      : false
                  }
                  onDelete={(e: any) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setDeletedId(state.items[rowIndex]?._id);
                    handleDeleteModal();
                  }}
                  hideDeleteButton={
                    hideOptionButtons && hideOptionButtons.delete
                      ? hideOptionButtons.delete.condition(
                          state.items[rowIndex]
                        )
                      : false
                  }
                  anotherButtons={showAnotherButtons(state.items[rowIndex])}
                />
              </>
            );
          },
        },
      },
    ];

    // Apply stored column display preferences
    return baseHeader.map((col, index) => {
      if (index in columnDisplayState && col.options) {
        return {
          ...col,
          options: {
            ...col.options,
            display: columnDisplayState[index],
          },
        };
      }
      return col;
    });
  }, [
    header,
    customStyle,
    state.page,
    state.perPage,
    state.items,
    hideOptionButtons,
    columnDisplayState,
  ]);

  const setColumnsInStorage = (tableTitle: string, tableStateColumns: any) => {
    const columnsToStore = tableStateColumns.map((column: any) => ({
      // Normalize display value to boolean for consistent storage
      display: column.display === "true" || column.display === true,
    }));

    const existingData = JSON.parse(
      localStorage.getItem("tableColumns") || "[]"
    );

    const updatedData = [
      ...existingData.filter((entry: any) => entry.tableTitle !== tableTitle),
      { tableTitle, columns: columnsToStore },
    ];
    localStorage.setItem("tableColumns", JSON.stringify(updatedData));

    // Update state to trigger re-render with new column visibility
    const newDisplayState: Record<number, boolean> = {};
    columnsToStore.forEach((column: any, index: number) => {
      newDisplayState[index] = column.display;
    });
    setColumnDisplayState(newDisplayState);
  };

  // Load column display preferences from localStorage on mount
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("tableColumns") || "[]");
    const tableData = data.find(
      (value: any) => value.tableTitle === tableTitle
    );

    if (tableData && tableData.columns) {
      const displayState: Record<number, boolean> = {};
      tableData.columns.forEach((column: any, index: number) => {
        // Handle both string and boolean values for display
        displayState[index] =
          typeof column.display === "string"
            ? column.display === "true"
            : Boolean(column.display);
      });
      setColumnDisplayState(displayState);
    }
  }, [tableTitle]);
  /**
   *
   * @returns
   */
  const getTableOptions = () => {
    const responsive: "standard" | "vertical" | "simple" | undefined =
      "standard";
    return {
      selectableRows:
        tableCustomOptions && tableCustomOptions.selectableRows
          ? tableCustomOptions.selectableRows
          : ("none" as any),
      viewColumns: true,
      responsive: responsive,
      rowsPerPageOptions: Config.rowsPerPage,
      confirmFilters: true,
      filter: false,
      onTableChange: (action: string, tableState: MUIDataTableState) => {
        if (action === "viewColumnsChange") {
          setColumnsInStorage(tableTitle, tableState.columns);
        }
      },
      print: false,
      download: false,
      expandableRows:
        tableCustomOptions && tableCustomOptions.expandableRows
          ? tableCustomOptions.expandableRows
          : false,
      expandableRowsHeader: false,
      textLabels: {
        body: {
          noMatch: Vocabulary.noResultsFound,
        },
        pagination: {
          next: Vocabulary.nextPage,
          previous: Vocabulary.previousPage,
          rowsPerPage: Vocabulary.rowsPerPage,
          displayRows: Vocabulary.of,
        },
        toolbar: {
          search: Vocabulary.search,
          downloadCsv: Vocabulary.downloadCSV,
          print: Vocabulary.print,
        },
        selectedRows: {
          text: Vocabulary.rowsSelected,
          delete: Vocabulary.delete,
        },
      },
      count: state.count,
      rowsPerPage: state.perPage,
      page: state.page,
      search:
        tableCustomOptions && tableCustomOptions.showSearch !== undefined
          ? tableCustomOptions.showSearch
          : true,
      serverSide: true,
      customToolbar: () => {
        return (
          <>
            {hideOptionButtons?.add ? null : (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  if (handleEdit) handleEdit(null);
                }}
                className={styles.addButtonTableHeader}
              >
                {Vocabulary.add}
              </Button>
            )}
            {headerButtons ? headerButtons : null}
          </>
        );
      },
      setRowProps: (row: any, dataIndex: any, rowIndex: any) => {
        return {
          style: handleEdit
            ? customStyle && customStyle.colorForRaw
              ? {
                  cursor: "pointer",
                  backgroundColor: customStyle.colorForRaw(
                    state.items[rowIndex]
                  ),
                }
              : {
                  cursor: "pointer",
                }
            : customStyle && customStyle.colorForRaw
            ? {
                backgroundColor: customStyle.colorForRaw(state.items[rowIndex]),
              }
            : {},
        };
      },
      onRowClick: (rowData: any, rowState: any) => {
        if (handleEdit) {
          if (
            state.items[rowState.rowIndex].status ===
            StockTransferStatuses.outtake
          )
            handleEdit(
              state.items[rowState.rowIndex],
              RenderAddComponent.outtake
            );
          else
            handleEdit(
              state.items[rowState.rowIndex],
              tableCustomOptions && tableCustomOptions.showItem
            );
        }
      },
      renderExpandableRow: (rowData: any, rowMeta: any) => {
        const data = state.items[rowMeta.dataIndex].children
          ? state.items[rowMeta.dataIndex].children
          : state.items[rowMeta.dataIndex];
        if (data.length === 0) return null;
        if (renderExpandableRow)
          return renderExpandableRow(rowData, rowMeta, data);
        return null;
      },
      onSearchChange: async (search: string | null) => {
        setState({ ...state, search: search });
        addParamToQueryString("search", search);
      },
      onSearchClose: async () => {
        removeParamFromQueryString(["search"]);
      },
      onChangePage: (page: any) => {
        addParamToQueryString("page", page);
        window.scrollTo(0, 0);
      },
      onColumnSortChange: (changedColumn: any, direction: any) => {
        if (tableCustomOptions && tableCustomOptions.enableSort)
          switch (changedColumn) {
            case "status":
              addParamToQueryString(
                "sort",
                `${changedColumn}.name,${direction}`
              );
              break;
            default:
              addParamToQueryString("sort", `${changedColumn},${direction}`);
              break;
          }
      },
      onChangeRowsPerPage: (numberOfRows: any) => {
        addParamToQueryString("page", 0);
        addParamToQueryString("perPage", numberOfRows);
        window.scrollTo(0, 0);
      },
      onRowSelectionChange: (
        currentRowsSelected: any,
        allRowsSelected: any
      ) => {
        if (onRowsSelect) {
          const selectedIds = allRowsSelected.map(
            (row: any) => state.items[row.dataIndex]._id
          );
          onRowsSelect(selectedIds);
        }
      },
      customToolbarSelect: () => {
        if (customToolbarSelect) return customToolbarSelect();
      },
      isRowExpandable: (dataIndex: any, expandedRows: any) => {
        if (isRowExpandable) {
          return isRowExpandable(dataIndex, expandedRows, state.items);
        }
        return false;
      },
    };
  };

  return (
    <>
      <div
        className={
          phoneStyle ? styles.tableContainerMobile : styles.tableContainer
        }
      >
        <MUIDataTableCustomStyle
          title={tableTitle}
          data={state.items}
          columns={tableHeader}
          options={getTableOptions()}
          style={
            customStyle && customStyle.tableStyle
              ? customStyle.tableStyle
              : null
          }
        />
      </div>
      {openDeleteModal ? (
        <GenericConfirmModal
          title={deleteMessages?.title || ""}
          text={deleteMessages?.text || ""}
          open={openDeleteModal}
          onClose={() => handleDeleteModal()}
          onSave={() => {
            deleteItem(deletedId || "");
            handleDeleteModal();
          }}
        ></GenericConfirmModal>
      ) : null}
    </>
  );
}
